import { create } from "zustand";
import type { SessionScope } from "../auth/session-scope.js";

/**
 * The one audit export this browser is waiting to download.
 *
 * Set when an administrator clicks Export CSV on Audit & compliance, cleared
 * the moment the CSV is fetched (or the job fails, expires or cannot be read).
 * There is no popup any more: the Audit page watches this job and downloads it
 * when it is ready, and the bell notification covers every other case.
 *
 * IN MEMORY ONLY, on purpose. It survives moving around the app — coming back
 * to the Audit page resumes the wait — but never a reload, so nothing can come
 * back after a refresh. No storage, no URL.
 *
 * KEYED BY SESSION, not tenant. Two administrators of the same tenant can
 * replace one another's session in place, and the admin tree never unmounts
 * across that (session-scope.ts), so a job is only ever acted on by the session
 * that asked for it. The scope is captured BEFORE the export request, so a
 * session swap while it is in flight stores the job under the asker.
 *
 * ONE FILE ACROSS TABS. The watch lives in the one tab that asked, but its bell
 * or email link can be opened in another, which downloads the file there. So
 * the link announces the job it took on a BroadcastChannel, and every other tab
 * of this browser drops a matching watch. A channel message is neither storage
 * nor the URL: it reaches only the tabs open now, and is gone. A tab listens
 * only while it watches a job. Best effort: where the browser refuses a
 * channel, each tab simply works alone.
 */
export interface AuditExportDownloadState {
  readonly scope: SessionScope;
  readonly jobId: string | null;
  watch(scope: NonNullable<SessionScope>, jobId: string): void;
  clear(): void;
}

export const useAuditExportDownload = create<AuditExportDownloadState>(
  (set) => ({
    scope: null,
    jobId: null,
    watch: (scope, jobId) => {
      set({ scope, jobId });
      listenForClaims();
    },
    clear: () => {
      set({ scope: null, jobId: null });
      stopListening();
    },
  }),
);

/** Tells this browser's other tabs that a link here took `jobId`'s file. */
export function announceAuditExportClaimed(jobId: string): void {
  const channel = openClaimsChannel();
  if (!channel) return;
  channel.postMessage({ jobId });
  channel.close();
}

const CLAIMS_CHANNEL = "playbookai.audit-export-claimed";
let listener: BroadcastChannel | null = null;

/* Constructing one can throw where storage access is partitioned away, as the
   shell's other channels note (pages/oauth/completion-channel.ts). */
function openClaimsChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    return new BroadcastChannel(CLAIMS_CHANNEL);
  } catch {
    return null;
  }
}

function listenForClaims(): void {
  if (listener) return;
  listener = openClaimsChannel();
  if (!listener) return;
  listener.onmessage = (event: MessageEvent<unknown>) => {
    const data = event.data as { readonly jobId?: unknown } | null;
    const state = useAuditExportDownload.getState();
    if (typeof data?.jobId === "string" && data.jobId === state.jobId) {
      state.clear();
    }
  };
}

function stopListening(): void {
  listener?.close();
  listener = null;
}
